import React, { useState } from 'react';
import theme from './ChangeRequestOverview.module.scss';
import { useAsyncState, useAsyncStateWithIsLoading, useQueryState, useParamReplace } from '@/utils/hooks';
import {
    getChangeRequestFilterData,
    searchChangeRequestsOverview,
    getChangeRequestParticipantsOverview,
} from '@/services/change-requests';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import ChangeRequestFilter, {
    getInitialChangeRequestFilterValue,
    ChangeRequestFilterValue,
} from '@/seller/components/change-requests/ChangeRequestFilter';
import ChangeRequestOverviewUnitItem from './ChangeRequestOverviewUnitItem';
import { FilterButtons, LoadingIndicator, useIsFilterModified } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { NoResultsCard } from '@/components/Cards';
import ChangeRequestOverviewContractorItem from './ChangeRequestOverviewContractorItem';
import { ButtonBar } from '@/components/Buttons';
import { Tabs } from '@ygtechnologies/rubus-ui';
import { Link } from 'react-router-dom';
import {
    ResidentialUnitGrouping,
    ResidentialUnitGroupingHeader,
} from '@/seller/components/residential-units/ResidentialUnitGrouping';
import { useParams } from 'react-router';

export enum ChangeRequestOverviewMode {
    ByContractor = 'contractor',
    ByUnit = 'unit',
}

interface ChangeRequestOverviewProps {}

const ChangeRequestOverview: React.FC<ChangeRequestOverviewProps> = () => {
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);
    const [filter, setFilter] = useQueryState<ChangeRequestFilterValue>(getInitialChangeRequestFilterValue());
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterData] = useAsyncState(() => getChangeRequestFilterData(projectId), [projectId], null);
    const [userOverviews] = useAsyncState(() => getChangeRequestParticipantsOverview(projectId), [projectId], null);
    const filterModified = useIsFilterModified(filter, getInitialChangeRequestFilterValue);

    const [changeRequests, , , isLoading] = useAsyncStateWithIsLoading(
        () =>
            searchChangeRequestsOverview({
                units: filter.units,
                groups: filter.groups,
                participants: filter.participants,
                searchText: filter.searchText,
                projectId,
            }),
        [projectId, filter],
        null
    );

    const { mode } = useParams<{ mode: ChangeRequestOverviewMode }>();

    const replaceParams = useParamReplace<{ mode: ChangeRequestOverviewMode }>();
    // const replaceParams = useParamReplace<{ mode: ChangeRequestOverviewMode }>();

    const renderContent = () => {
        if (isLoading || changeRequests == null) {
            return <LoadingIndicator />;
        }

        if (changeRequests.length === 0) {
            return <NoResultsCard />;
        }

        if (mode === ChangeRequestOverviewMode.ByUnit) {
            return (
                <div className={theme.relative}>
                    <ResidentialUnitGrouping projectId={projectId} items={changeRequests} emptyText={<div />}>
                        {(items, residentialUnit, residentialUnitGroup) => (
                            <React.Fragment key={residentialUnit.id}>
                                <ResidentialUnitGroupingHeader
                                    residentialUnit={residentialUnit}
                                    residentialUnitGroup={residentialUnitGroup}
                                    detailsLinkTab="changes"
                                />
                                {items!.map((cr) => (
                                    <ChangeRequestOverviewUnitItem key={cr.changeRequestId} changeRequest={cr} />
                                ))}
                            </React.Fragment>
                        )}
                    </ResidentialUnitGrouping>
                </div>
            );
        } else if (mode === ChangeRequestOverviewMode.ByContractor) {
            return (
                <div className={theme.relative}>
                    {userOverviews?.map((userOverview) => (
                        <ChangeRequestOverviewContractorItem
                            key={userOverview.userId}
                            userOverview={userOverview}
                            changeRequests={changeRequests.filter((cr) =>
                                cr.participants.map((p) => p.userId).includes(userOverview.userId)
                            )}
                        />
                    ))}
                </div>
            );
        }

        return null;
    };

    return (
        <>
            <ButtonBar>
                <Tabs activeId={mode} variant="pill" size="sm">
                    <Tabs.Tab
                        id={ChangeRequestOverviewMode.ByUnit}
                        as={Link}
                        to={replaceParams({ mode: ChangeRequestOverviewMode.ByUnit })}
                        label={<Trans>By unit</Trans>}
                    />
                    <Tabs.Tab
                        id={ChangeRequestOverviewMode.ByContractor}
                        as={Link}
                        to={replaceParams({ mode: ChangeRequestOverviewMode.ByContractor })}
                        label={<Trans>By contractor</Trans>}
                    />
                </Tabs>

                <ButtonBar.Right>
                    <ChangeRequestFilter
                        hideStatuses
                        isOpen={isFilterOpen}
                        onClose={() => setIsFilterOpen(false)}
                        filterData={filterData}
                        value={filter}
                        onFilterChange={setFilter}
                    />

                    <FilterButtons
                        searchText={filter.searchText}
                        onSearchTextChange={(v) => setFilter({ ...filter, searchText: v })}
                        isLoading={isLoading}
                        onButtonClick={() => setIsFilterOpen((v) => !v)}
                        isActive={filterModified}
                    />
                </ButtonBar.Right>
            </ButtonBar>

            {renderContent()}
        </>
    );
};

export default ChangeRequestOverview;
